// Map3D_SceneLights — Set15 P7 (§3.8): the real scene lights the 3D NEAR-tier scenery meshes
// (MeshStandardMaterial) render under.
//
// The far-tier impostor atlas is PRE-LIT (Set13 §4.4 / Set14 P4 §4.5): the bake renders the
// variant meshes under a `DirectionalLight` + `AmbientLight` derived from the terrain's shading
// config, and the atlas stores final shaded RGBA. The near-tier REAL meshes (Set15 P7) render
// LIVE, so they need that SAME light rig in the 3D scene — otherwise a near tree is lit by the
// baked impostor's sun but its real trunk/canopy are lit by whatever the scene happens to have
// (a huge mismatch at the near/far boundary). This component installs the same
// `DirectionalLight` + `AmbientLight` the bake used (from the terrain `TerrainHeightShadingConfig`
// of the first terrain mod), so a near mesh and the far impostor share a shading model.
//
// Only mounted in 3D mode (the §5 allow-list). The bake's light rig is defined by `BakeLightRig`
// {azimuth, altitude, ambient, strength}; the DirectionalLight points TOWARD the sun at
// `bakeLightDir(rig)` (the terrain hillshade convention) and the AmbientLight intensity = `ambient`.
import {useFrame, useThree} from "@react-three/fiber";
import {useEffect, useRef} from "react";
import * as THREE from "three";
import {type BakeLightRig, bakeLightDir} from "../../../../Terrain/Scenery/Bake/ImpostorBaker.js";
import {setTerrainShadowLight, setTerrainShadowMapSize} from "../../../../Terrain/Heights/TerrainShadows.js";
import {type MapObject} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {type Mod_TerrainDefinition, TerrainHeightShadingConfig} from "../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_TerrainDefinition.js";
import {observer_mgl_r3} from "../../../../Utils/ThreeJS/General.js";

/** Collect the enabled terrain-definition mods (mirrors TerrainRenderer / SceneryRenderer). */
function terrainModsOf(mapObjects: MapObject[]): Mod_TerrainDefinition[] {
	const mods: Mod_TerrainDefinition[] = [];
	for (const obj of mapObjects) {
		for (const mod of obj.modifiers ?? []) {
			if (mod.enabled && mod.terrainDefinition) mods.push(mod.terrainDefinition);
		}
	}
	return mods;
}

/** Set15 P7 — the near-tier scene lights, derived from the terrain shading config (the same
 *  fields the bake uses). Re-created when the shading config changes (a light edit re-lights the
 *  live near meshes immediately; the far impostor re-bakes on the same signature change).
 *  Set15 P8 (§6.2) — this component also owns the SHADOW setup: the cast-shadow directional light
 *  + renderer `gl.shadowMap` state + an orthographic shadow camera over the near field. The shadow
 *  light is pushed to the module-global `terrainShadowLight_current` so the terrain renderer can
 *  set per-chunk shadow uniforms (it must NOT import this component). */
export const Map3D_SceneLights = observer_mgl_r3((props: {mapObjects: MapObject[], shadowSettings: {enabled: boolean, mapSize: number, radius: number, bias: number}})=>{
	const {scene, gl, camera} = useThree();
	const firstMod = terrainModsOf(props.mapObjects)[0];
	const resolvedShadingCfg = firstMod?.heightShadingConfig ?? new TerrainHeightShadingConfig();
	const {enabled: shadowsEnabled, mapSize: shadowMapSize, radius: shadowRadius, bias: shadowBias} = props.shadowSettings;
	// Refs bridged between the effect (creates the shadow light) and the useFrame (re-anchors it
	// to the live camera each frame) — Set15 P8 fix.
	const shadowLightRef = useRef<THREE.DirectionalLight | null>(null);
	const shadowTargetRef = useRef<THREE.Object3D | null>(null);
	const directionalOffsetRef = useRef<THREE.Vector3 | null>(null);

	useEffect(()=>{
		const rig: BakeLightRig = {
			azimuth: resolvedShadingCfg.light_azimuth,
			altitude: resolvedShadingCfg.light_altitude,
			ambient: resolvedShadingCfg.ambient,
			strength: resolvedShadingCfg.strength,
		};
		// Unit vector pointing TOWARD the sun (the terrain `computeLightDir` convention). The
		// light's world offset from its target is a FIXED direction scaled by a large distance —
		// the direction is constant, only the anchor (the shadow camera's ortho centre) moves with
		// the camera (see the useFrame below).
		const [lx, ly, lz] = bakeLightDir(rig);
		const directional = new THREE.DirectionalLight(0xffffff, rig.strength);
		const directionalOffset = new THREE.Vector3(lx, ly, lz).multiplyScalar(100);
		directional.position.copy(directionalOffset);
		directionalOffsetRef.current = directionalOffset;
		const ambient = new THREE.AmbientLight(0xffffff, rig.ambient);
		scene.add(directional, ambient);

		// Set15 P8 — shadow mapping on the directional light.  The light target must be in the
		// scene for three's `updateMatrices` to resolve it.  One tight orthographic shadow camera
		// over the near field (the locked camera pins where the legible near field is — §6.2).
		let shadowLight: THREE.DirectionalLight | null = null;
		let shadowTarget: THREE.Object3D | null = null;
		if (shadowsEnabled) {
			directional.castShadow = true;
			const size = shadowMapSize;
			directional.shadow.mapSize.set(size, size);
			directional.shadow.camera.near = 1;
			directional.shadow.camera.far = 2000;
			directional.shadow.camera.left = -shadowRadius;
			directional.shadow.camera.right = shadowRadius;
			directional.shadow.camera.top = shadowRadius;
			directional.shadow.camera.bottom = -shadowRadius;
			// ⚠ The bias MUST be NEGATIVE to fight acne. Three's convention (shadowmap_pars_fragment
			// + our getShadowMask, which mirrors it) is `shadowCoord.z += shadowBias` followed by
			// `step(shadowCoord.z, depth)` → 1.0 (LIT) when z <= depth. So a POSITIVE bias pushes
			// the receiver's depth FURTHER from the light, making the compare fail more often =
			// MORE self-shadowing. The old +0.0005 therefore made the terrain shadow ITSELF
			// everywhere (uniform acne) instead of suppressing it. Negative pulls z toward the
			// light, which is what "bias against acne" means. (three's own default is 0, and its
			// docs/examples use small NEGATIVE values like -0.0001 for exactly this reason.)
			// Ortho-cascade depth range here is near 1 → far 2000, so the normalized-depth
			// quantum is large; -0.0015 clears the acne without visible peter-panning.
			// Set15 P8 — the bias is a user knob (`view3D_shadowBias`, default -0.0015) so it can
			// be tuned live; the default preserves the proven anti-acne value.
			directional.shadow.bias = shadowBias;
			shadowTarget = new THREE.Object3D();
			shadowTarget.position.set(0, 0, 0);
			scene.add(shadowTarget);
			directional.target = shadowTarget;
			shadowLight = directional;
			shadowLightRef.current = shadowLight;
			shadowTargetRef.current = shadowTarget;
			// Push to the module-global so the terrain renderer can set per-chunk shadow uniforms.
			setTerrainShadowLight(shadowLight);
			setTerrainShadowMapSize(size);
			// Enable the renderer's shadow map (PCFSoft for a soft edge) — three renders the cast
			// depth maps + the standard shadows; our custom ShaderMaterial reads them manually.
			gl.shadowMap.enabled = true;
			gl.shadowMap.type = THREE.PCFSoftShadowMap;
		} else {
			setTerrainShadowLight(null);
			shadowLightRef.current = null;
			shadowTargetRef.current = null;
			// ⚠ CRITICAL — reset the renderer's shadow map here. This component sets
			// `gl.shadowMap.enabled = true` in the shadows-on branch; if we DON'T reset it here,
			// it LEAKS true for the renderer's lifetime even after the user toggles shadows off.
			// With `gl.shadowMap.enabled` leaked true + the near-tier meshes' unconditional
			// `castShadow`/`receiveShadow`, three keeps running the shadow-receive path in
			// shadows-off, which was breaking scenery rendering (the reported "trees vanish even
			// with 3D shadows disabled" bug).
			gl.shadowMap.enabled = false;
		}

		return ()=>{
			shadowLightRef.current = null;
			shadowTargetRef.current = null;
			directionalOffsetRef.current = null;
			if (shadowTarget) scene.remove(shadowTarget);
			if (shadowLight) {
				shadowLight.shadow.map?.dispose?.();
			}
			scene.remove(directional, ambient);
			directional.dispose?.();
			setTerrainShadowLight(null);
			// Reset the shadow-map state on teardown too (e.g. leaving 3D / Canvas remount), so a
			// reused renderer (2D or another mode) doesn't inherit leaked shadow state.
			gl.shadowMap.enabled = false;
		};
	}, [scene, gl,
		resolvedShadingCfg.light_azimuth, resolvedShadingCfg.light_altitude,
		resolvedShadingCfg.ambient, resolvedShadingCfg.strength,
		shadowsEnabled, shadowMapSize, shadowRadius, shadowBias,
	]);

	// Set15 P8 FIX — the shadow light/camera must follow the ACTIVE camera, not sit parked at the
	// world origin. The ortho shadow camera is ±shadowRadius around its target; if the target stays
	// at (0,0,0) while the viewer's camera (and the scenery) is thousands of metres away, the whole
	// shadow map covers empty ground and NO shadow ever appears. Each frame we re-anchor the shadow
	// light's target to the live camera's XZ position (kept below the light's fixed sun direction),
	// so the ±shadowRadius box always covers the visible near field. Cheap: a single vector write +
	// a matrix/update on the one directional light, all shadowing is off when shadows are disabled.
	useFrame(()=>{
		if (!shadowsEnabled) return;
		const light = shadowLightRef.current, target = shadowTargetRef.current, offset = directionalOffsetRef.current;
		if (light == null || target == null || offset == null) return;
		const cam = camera;
		// Anchor the shadow camera at the viewer — shadows are cast onto the terrain/scenery
		// around the eye. Use the camera position directly (elevation too); the ortho box is
		// ±radius around it, and the light offset keeps the sun's fixed direction.
		target.position.copy(cam.position);
		light.position.copy(offset).add(target.position);
	});

	return null;
});
